// SenhaSeguranca.js — Seção "Senha e Segurança".
// Subtelas: menu (Alterar Senha / Remover Conta) e formulário de troca de senha.
// Troca de senha e remoção de conta usam validação local + toast; a chamada real
// ao backend está marcada como TODO (endpoints ainda não definidos no apiConfig).
import React, { useState } from "react";
import useProfileData from "../../../hooks/userProfileData";
import { SectionCard, Field, PrimaryButton, GhostButton, DangerButton, Divider, BackHeader, Toast, useToast } from "../ui/ProfileUI";

// Regras de senha (refletem o mockup)
const REGRAS = [
  { key: "maiuscula", label: "1 letra maiúscula", test: (s) => /[A-Z]/.test(s) },
  { key: "especial", label: "1 caractere especial @#$%¨&", test: (s) => /[@#$%¨&!*?.\-_]/.test(s) },
  { key: "min", label: "Mínimo de 8 caracteres", test: (s) => s.length >= 8 },
];

// Campo de senha com botão mostrar/ocultar
const PasswordInput = ({ value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-lg bg-insigne-navy-deep border border-line px-4 py-3 pr-11 text-sm text-white placeholder:text-white/40 outline-none focus:border-insigne-navy-light focus:ring-1 focus:ring-insigne-navy-light"
      />
      <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white" aria-label="Mostrar senha">
        <i className={"bi " + (show ? "bi-eye-slash" : "bi-eye")} />
      </button>
    </div>
  );
};

const SenhaSeguranca = ({ onDeleted }) => {
  const { profileData } = useProfileData();
  const { toast, fireToast } = useToast();
  const [view, setView] = useState("menu"); // "menu" | "senha"
  const [form, setForm] = useState({ atual: "", nova: "", repetir: "" });
  const [showModal, setShowModal] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const regrasOk = REGRAS.every((r) => r.test(form.nova));

  const salvarSenha = async () => {
    if (!form.atual) return fireToast("Informe sua senha atual.", "error");
    if (!regrasOk) return fireToast("A nova senha não cumpre os requisitos.", "error");
    if (form.nova !== form.repetir) return fireToast("As senhas não coincidem.", "error");
    try {
      setSaving(true);
      // TODO: chamar endpoint de troca de senha quando existir, ex.:
      // await axios.put(`${API_URL}api/usuario-editora/senha`, { senha_atual, nova_senha }, { headers })
      await new Promise((r) => setTimeout(r, 500));
      fireToast("Senha alterada com sucesso!");
      setForm({ atual: "", nova: "", repetir: "" });
      setView("menu");
    } finally {
      setSaving(false);
    }
  };

  const emailCadastrado = profileData?.email || "";
  const podeRemover = emailConfirm.trim().toLowerCase() === emailCadastrado.trim().toLowerCase() && !!emailCadastrado;

  const removerConta = () => {
    if (!podeRemover) { fireToast("O e-mail não confere com o da conta.", "error"); return; }
    // TODO: chamar endpoint de remoção de conta quando existir.
    localStorage.removeItem("token");
    setShowModal(false);
    fireToast("Conta removida.");
    if (onDeleted) setTimeout(onDeleted, 800);
  };

  // ===== Formulário de troca de senha =====
  if (view === "senha") {
    return (
      <SectionCard>
        <Toast {...toast} />
        <BackHeader title="Alterar Senha" onBack={() => setView("menu")} />
        <div className="max-w-xl flex flex-col gap-5">
          <Field label="Senha Atual"><PasswordInput value={form.atual} onChange={set("atual")} placeholder="Sua senha atual" /></Field>
          <Field label="Nova Senha"><PasswordInput value={form.nova} onChange={set("nova")} placeholder="Nova senha" /></Field>
          <Field label="Repetir Nova Senha"><PasswordInput value={form.repetir} onChange={set("repetir")} placeholder="Repita a nova senha" /></Field>

          <ul className="flex flex-col gap-2 mt-1">
            {REGRAS.map((r) => {
              const ok = r.test(form.nova);
              return (
                <li key={r.key} className={"flex items-center gap-2 text-sm " + (ok ? "text-emerald-400 font-semibold" : "text-white/70")}>
                  <span className={"inline-block w-1.5 h-1.5 rounded-full " + (ok ? "bg-emerald-400" : "bg-white/40")} />
                  {r.label}
                </li>
              );
            })}
          </ul>

          <div className="flex justify-end">
            <PrimaryButton onClick={salvarSenha} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</PrimaryButton>
          </div>
        </div>
      </SectionCard>
    );
  }

  // ===== Menu (Alterar Senha / Remover Conta) =====
  return (
    <>
      <SectionCard title="Senha e Segurança">
      <Toast {...toast} />

      <div className="flex items-center justify-between gap-4 py-4">
        <div>
          <p className="text-white font-medium">Nova Senha</p>
          <p className="text-white/55 text-sm">Altere a senha de acesso da sua conta.</p>
        </div>
        <PrimaryButton onClick={() => setView("senha")}>Alterar Senha</PrimaryButton>
      </div>

      <Divider />

      <div className="flex items-center justify-between gap-4 py-4">
        <div>
          <p className="text-white font-medium">Apagar Conta</p>
          <p className="text-white/55 text-sm">Remove permanentemente sua conta e seus dados.</p>
        </div>
        <DangerButton onClick={() => { setEmailConfirm(""); setShowModal(true); }}>Remover Conta</DangerButton>
      </div>

      </SectionCard>

      {/* Modal de remoção de conta. FORA do SectionCard: ver a nota igual em
          EnderecosEntrega.js — o `Reveal` do cartão põe `transform` nele
          durante a entrada, e transform contém `position: fixed`. */}
      {showModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-lg rounded-2xl border border-line bg-insigne-navy-deep p-6 relative">
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-white/50 hover:text-white" aria-label="Fechar">
              <i className="bi bi-x-lg" />
            </button>
            <h3 className="text-lg font-bold text-white">Deseja remover sua conta?</h3>
            <p className="text-white/60 text-sm mt-2 leading-relaxed">
              Essa ação não poderá ser desfeita, recomendamos cautela ao confirmar essa ação.
              Todas as informações associadas à sua conta serão perdidas.
            </p>
            <div className="mt-5">
              <label className="text-sm font-medium text-white">Insira seu e-mail</label>
              <input
                type="email"
                value={emailConfirm}
                onChange={(e) => setEmailConfirm(e.target.value)}
                placeholder="seu@email.com"
                className="mt-2 w-full rounded-lg bg-insigne-navy-deep border border-line px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none focus:border-insigne-navy-light focus:ring-1 focus:ring-insigne-navy-light"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <GhostButton onClick={() => setShowModal(false)}>Cancelar</GhostButton>
              <DangerButton onClick={removerConta} disabled={!podeRemover}>
                <i className="bi bi-trash" /> Remover Conta
              </DangerButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SenhaSeguranca;
